import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Timer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { EXPIRY_KEY, CART_KEY, releaseCart } from "@/lib/reservation";

const readCartCount = () => {
  try {
    const items = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    return items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
  } catch { return 0; }
};
const readExpiry = () => {
  const v = localStorage.getItem(EXPIRY_KEY);
  return v ? new Date(v).getTime() : 0;
};

/** Global cart-reservation countdown. Rendered once at the app root so it shows
 *  on every page (including pages with no cart, e.g. The Essence). It only
 *  appears when the shopper has active reserved items. On expiry it clears the
 *  cart, releases the server locks, and notifies the shopper. */
export default function CartCountdownBadge() {
  const location = useLocation();
  const { user } = useAuth();
  const [count, setCount] = useState(readCartCount());
  const [expiry, setExpiry] = useState(readExpiry());
  const [remaining, setRemaining] = useState(0);

  const sync = useCallback(() => {
    setCount(readCartCount());
    setExpiry(readExpiry());
  }, []);

  useEffect(() => {
    window.addEventListener("storage", sync);
    window.addEventListener("cart-updated", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("cart-updated", sync);
    };
  }, [sync]);

  useEffect(() => {
    const tick = () => {
      const exp = readExpiry();
      const cnt = readCartCount();
      setExpiry(exp); setCount(cnt);
      if (exp && cnt > 0) {
        const left = Math.max(0, Math.floor((exp - Date.now()) / 1000));
        setRemaining(left);
        if (left <= 0) {
          // Expired — release locks, clear cart, notify.
          localStorage.removeItem(CART_KEY);
          localStorage.removeItem(EXPIRY_KEY);
          releaseCart();
          window.dispatchEvent(new Event("cart-expired"));
          window.dispatchEvent(new Event("cart-updated"));
          toast.error("Your cart reservation expired — items have been released back to inventory.", { duration: 8000 });
          setCount(0); setExpiry(0); setRemaining(0);
        }
      } else {
        setRemaining(0);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!(count > 0 && expiry && remaining > 0)) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const low = remaining <= 60;
  const isSubStore = /^\/shop\/[^/]+/.test(location.pathname);
  if (location.pathname === "/order") return null;

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none"
      data-testid="cart-countdown-badge"
    >
      <div
        className={`pointer-events-auto flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg border backdrop-blur
          ${low ? "bg-red-600 text-white border-red-500 animate-pulse" : isSubStore ? "bg-white/95 text-slate-800 border-red-500 ring-2 ring-red-500/70" : "bg-[#0B0E17]/92 text-white border-[#D4AF37]/50"}`}
      >
        {low ? <AlertTriangle size={15} className="text-white" /> : <Timer size={15} className={isSubStore ? "text-red-600" : "text-[#D4AF37]"} />}
        <span className="text-[11px] uppercase tracking-wider font-semibold hidden sm:inline opacity-80">Cart reserved</span>
        <span className={`font-bold tabular-nums text-sm ${isSubStore ? "text-red-600" : ""}`} data-testid="cart-countdown-time">{mm}:{ss}</span>
        <span className="text-[11px] opacity-80">· {count} item{count === 1 ? "" : "s"}</span>
      </div>
    </div>
  );
}
